/**
 * Bridge Manager
 * 
 * Central manager for all bridge communication between Web Frame and Quapp SDK.
 * Handles:
 * - postMessage communication with iframe
 * - Routing calls to appropriate bridges
 * - Managing subscriptions for streaming data
 * - Triggering install prompts for unavailable bridges
 */

import { BaseBridge } from './BaseBridge'
import type {
  BridgeRequest,
  BridgeResponse,
  BridgeError,
  BridgeConfig,
  BridgeInfo,
  BridgeSubscribeRequest,
  BridgeUnsubscribeRequest,
  BridgeDataUpdate,
  StoreUrls,
} from './types'

// Import all bridges
import { DeviceInfoBridge } from './info/DeviceInfoBridge'
import { BatteryBridge } from './info/BatteryBridge'
import { NetworkBridge } from './info/NetworkBridge'
import { StorageBridge } from './info/StorageBridge'
import { GyroscopeBridge } from './sensors/GyroscopeBridge'
import { LocationBridge } from './sensors/LocationBridge'
import { FlashlightBridge } from './hardware/FlashlightBridge'
import { StatusBarBridge } from './hardware/StatusBarBridge'
import { PaymentBridge } from './ui/PaymentBridge'
import { FileBridge } from './ui/FileBridge'
import { BiometricBridge } from './auth/BiometricBridge'

export interface BridgeManagerOptions {
  /** Callback when an unavailable bridge is called */
  onShowInstallPrompt?: (feature: string) => void
  /** Store URLs for install prompts (fetched from app_config) */
  storeUrls?: StoreUrls
  /** Whether install prompts are enabled */
  promptEnabled?: boolean
}

/** SDK version info from connected Quapp */
interface ConnectedSDK {
  version: string
  firstSeen: Date
  lastSeen: Date
}

export class BridgeManager {
  private bridges: Map<string, BaseBridge> = new Map()
  private iframeWindow: Window | null = null
  private iframeOrigin: string | null = null
  private messageHandler: ((event: MessageEvent) => void) | null = null
  private config: BridgeConfig
  
  // SDK version tracking for backward compatibility
  private connectedSDK: ConnectedSDK | null = null
  
  // Web Frame protocol version
  static readonly WEB_FRAME_VERSION = '1.0.0'
  
  // Minimum SDK version for specific features (for future use)
  static readonly MIN_SDK_VERSIONS = {
    touchEmulation: '1.2.1',
    versionedMessages: '1.2.3',
  }
  
  // Callbacks
  private onShowInstallPrompt?: (feature: string) => void
  
  constructor(options: BridgeManagerOptions = {}) {
    this.onShowInstallPrompt = options.onShowInstallPrompt
    this.config = {
      storeUrls: options.storeUrls || { android: null, ios: null },
      promptEnabled: options.promptEnabled ?? true
    }
    
    console.log('[BridgeManager] Initializing Web Frame v' + BridgeManager.WEB_FRAME_VERSION)
    
    this.initializeBridges()
    this.setupMessageListener()
  }
  
  /**
   * Initialize all bridges
   */
  private initializeBridges(): void {
    // Info bridges (full support)
    this.registerBridge(new DeviceInfoBridge())
    this.registerBridge(new BatteryBridge())
    this.registerBridge(new NetworkBridge())
    this.registerBridge(new StorageBridge())
    
    // Sensor bridges (full support)
    this.registerBridge(new GyroscopeBridge(this.sendDataUpdate.bind(this)))
    this.registerBridge(new LocationBridge(this.sendDataUpdate.bind(this)))
    
    // Hardware bridges (partial/unavailable)
    this.registerBridge(new FlashlightBridge())
    this.registerBridge(new StatusBarBridge())
    
    // UI bridges (partial/unavailable)
    this.registerBridge(new PaymentBridge())
    this.registerBridge(new FileBridge())
    
    // Auth bridges (partial)
    this.registerBridge(new BiometricBridge())
  }
  
  /**
   * Register a single bridge
   */
  private registerBridge(bridge: BaseBridge): void {
    bridge.initialize().catch(err => {
      console.warn(`Failed to initialize bridge ${bridge.bridgeName}:`, err)
    })
    this.bridges.set(bridge.bridgeName, bridge)
  }
  
  /**
   * Setup message listener for postMessage communication
   */
  private setupMessageListener(): void {
    this.messageHandler = this.handleMessage.bind(this)
    window.addEventListener('message', this.messageHandler)
  }
  
  /**
   * Handle incoming postMessage from iframe
   */
  private async handleMessage(event: MessageEvent): Promise<void> {
    // Validate origin if set
    if (this.iframeOrigin && event.origin !== this.iframeOrigin) {
      return
    }
    
    const data = event.data
    if (!data || typeof data !== 'object') return
    
    // Track SDK version from messages (v1.2.3+)
    if (data.sdkVersion) {
      this.trackSDKVersion(data.sdkVersion)
    }
    
    // Route based on message type
    switch (data.type) {
      case 'QUAPP_BRIDGE_CALL':
        await this.handleBridgeCall(event.source as Window, data as BridgeRequest)
        break
      case 'QUAPP_BRIDGE_SUBSCRIBE':
        await this.handleSubscribe(event.source as Window, data as BridgeSubscribeRequest)
        break
      case 'QUAPP_BRIDGE_UNSUBSCRIBE':
        this.handleUnsubscribe(data as BridgeUnsubscribeRequest)
        break
      case 'QUAPP_SDK_INIT':
        // SDK is requesting ready state (may have missed initial ready message)
        console.log('[BridgeManager] SDK init request received', data.sdkVersion ? `v${data.sdkVersion}` : '(legacy)')
        if (event.source) {
          this.signalReady(event.source as Window)
        }
        break
    }
  }
  
  /**
   * Track connected SDK version for backward compatibility
   */
  private trackSDKVersion(version: string): void {
    const now = new Date()
    
    if (!this.connectedSDK || this.connectedSDK.version !== version) {
      // New SDK or version changed
      console.log(`[BridgeManager] 📱 SDK Connected: v${version}`)
      
      if (this.connectedSDK && this.connectedSDK.version !== version) {
        console.log(`[BridgeManager] ⚠️ SDK version changed: v${this.connectedSDK.version} → v${version}`)
      }
      
      this.connectedSDK = {
        version,
        firstSeen: now,
        lastSeen: now
      }
      
      // Log compatibility info
      this.logCompatibilityInfo(version)
    } else {
      this.connectedSDK.lastSeen = now
    }
  }
  
  /**
   * Log compatibility information for connected SDK
   */
  private logCompatibilityInfo(sdkVersion: string): void {
    const features = []
    const missing = []
    
    for (const [feature, minVersion] of Object.entries(BridgeManager.MIN_SDK_VERSIONS)) {
      if (this.compareVersions(sdkVersion, minVersion) >= 0) {
        features.push(feature)
      } else {
        missing.push(`${feature} (requires v${minVersion}+)`)
      }
    }
    
    if (features.length > 0) {
      console.log(`[BridgeManager] ✅ SDK features available: ${features.join(', ')}`)
    }
    if (missing.length > 0) {
      console.log(`[BridgeManager] ⚠️ SDK features unavailable: ${missing.join(', ')}`)
    }
  }
  
  /**
   * Compare semantic versions (returns -1, 0, or 1)
   */
  private compareVersions(a: string, b: string): number {
    const partsA = a.split('.').map(Number)
    const partsB = b.split('.').map(Number)
    
    for (let i = 0; i < 3; i++) {
      const numA = partsA[i] || 0
      const numB = partsB[i] || 0
      if (numA > numB) return 1
      if (numA < numB) return -1
    }
    return 0
  }
  
  /**
   * Check if connected SDK supports a specific feature
   */
  supportsFeature(feature: keyof typeof BridgeManager.MIN_SDK_VERSIONS): boolean {
    if (!this.connectedSDK) return false
    const minVersion = BridgeManager.MIN_SDK_VERSIONS[feature]
    return this.compareVersions(this.connectedSDK.version, minVersion) >= 0
  }
  
  /**
   * Get connected SDK info
   */
  getConnectedSDKInfo(): ConnectedSDK | null {
    return this.connectedSDK
  }
  
  /**
   * Handle a bridge method call
   */
  private async handleBridgeCall(source: Window, request: BridgeRequest): Promise<void> {
    const { bridgeName, method, args, callId } = request
    
    try {
      const bridge = this.bridges.get(bridgeName)
      
      if (!bridge) {
        throw {
          code: 'BRIDGE_NOT_FOUND',
          message: `Bridge '${bridgeName}' is not available`,
          action: 'SHOW_INSTALL_PROMPT'
        } as BridgeError
      }
      
      // For unavailable bridges, skip method check and go straight to handleCall
      // This will throw SHOW_INSTALL_PROMPT for any method call
      if (bridge.availability === 'unavailable') {
        // Let handleCall throw the unavailable error with install prompt action
        const result = await bridge.handleCall(method, args)
        // This won't be reached for UnavailableBridge
        this.sendResponse(source, {
          type: 'QUAPP_BRIDGE_RESPONSE',
          callId,
          success: true,
          result
        })
        return
      }
      
      // For available bridges, check if method exists
      if (!bridge.isMethodAvailable(method)) {
        throw {
          code: 'METHOD_NOT_FOUND',
          message: `Method '${method}' not found on bridge '${bridgeName}'`,
          action: 'NONE'
        } as BridgeError
      }
      
      const result = await bridge.handleCall(method, args)
      
      this.sendResponse(source, {
        type: 'QUAPP_BRIDGE_RESPONSE',
        callId,
        success: true,
        result
      })
      
    } catch (error: any) {
      // Handle install prompt action
      if (error.action === 'SHOW_INSTALL_PROMPT' && this.config.promptEnabled) {
        this.onShowInstallPrompt?.(bridgeName)
      }
      
      this.sendResponse(source, {
        type: 'QUAPP_BRIDGE_RESPONSE',
        callId,
        success: false,
        error: this.normalizeError(error)
      })
    }
  }
  
  /**
   * Handle subscription request for streaming bridges
   */
  private async handleSubscribe(source: Window, request: BridgeSubscribeRequest): Promise<void> {
    const { bridgeName, subscriptionId, options } = request
    
    const bridge = this.bridges.get(bridgeName) as any
    
    if (bridge?.startStreaming) {
      this.iframeWindow = source
      bridge.startStreaming(subscriptionId, options)
    }
  }
  
  /**
   * Handle unsubscribe request
   */
  private handleUnsubscribe(request: BridgeUnsubscribeRequest): void {
    const { subscriptionId } = request
    
    // Find the bridge that owns this subscription and stop it
    for (const bridge of this.bridges.values()) {
      if ((bridge as any).stopStreaming) {
        (bridge as any).stopStreaming(subscriptionId)
      }
    }
  }
  
  /**
   * Send data update to iframe (for streaming bridges)
   */
  private sendDataUpdate(subscriptionId: string, data: any): void {
    if (!this.iframeWindow) return
    
    const update: BridgeDataUpdate = {
      type: 'QUAPP_BRIDGE_DATA',
      subscriptionId,
      data
    }
    
    this.iframeWindow.postMessage(update, '*')
  }
  
  /**
   * Send response to iframe
   */
  private sendResponse(target: Window, response: BridgeResponse): void {
    target.postMessage(response, '*')
  }
  
  /**
   * Normalize error to BridgeError format
   */
  private normalizeError(error: any): BridgeError {
    if (error && typeof error === 'object' && 'code' in error) {
      return error as BridgeError
    }
    
    return {
      code: 'INTERNAL_ERROR',
      message: error?.message || 'An unexpected error occurred',
      action: 'NONE'
    }
  }
  
  /**
   * Set the iframe origin for validation
   */
  setIframeOrigin(origin: string): void {
    this.iframeOrigin = origin
  }
  
  /**
   * Update store URLs (can be called after fetching from app_config)
   */
  updateStoreUrls(urls: StoreUrls): void {
    this.config.storeUrls = urls
  }
  
  /**
   * Get store URLs
   */
  getStoreUrls(): StoreUrls {
    return this.config.storeUrls
  }
  
  /**
   * Signal to the iframe that Web Frame is ready
   */
  signalReady(iframeWindow: Window): void {
    this.iframeWindow = iframeWindow
    
    console.log('[BridgeManager] Signaling ready to iframe with bridges:', this.getAvailableBridges())
    
    iframeWindow.postMessage({
      type: 'QUAPP_WEB_FRAME_READY',
      version: BridgeManager.WEB_FRAME_VERSION,
      bridges: this.getAvailableBridges(),
      bridgeAvailability: this.getBridgeAvailability(), // true = fully available, false = will show install prompt
      capabilities: this.getCapabilities()
    }, '*')
  }
  
  /**
   * Get list of available bridges
   * NOTE: We include ALL bridges (even unavailable ones) so the SDK knows about them.
   * This allows calls to unavailable bridges to reach the Web Frame,
   * which can then show the install prompt overlay.
   */
  getAvailableBridges(): string[] {
    // Return ALL bridge names - the Web Frame will handle unavailable ones
    // by throwing an error with action: 'SHOW_INSTALL_PROMPT'
    return Array.from(this.bridges.keys())
  }
  
  /**
   * Get list of bridges with full availability (not just names)
   */
  getBridgeAvailability(): Record<string, boolean> {
    const availability: Record<string, boolean> = {}
    
    for (const [name, bridge] of this.bridges) {
      availability[name] = bridge.availability !== 'unavailable'
    }
    
    return availability
  }
  
  /**
   * Get all bridge info
   */
  getAllBridgeInfo(): BridgeInfo[] {
    return Array.from(this.bridges.values()).map(bridge => bridge.getBridgeInfo())
  }
  
  /**
   * Get capabilities object for SDK
   */
  getCapabilities(): Record<string, boolean> {
    const capabilities: Record<string, boolean> = {}
    
    for (const bridge of this.bridges.values()) {
      const bridgeCaps = bridge.getCapabilities()
      for (const [method, cap] of Object.entries(bridgeCaps)) {
        capabilities[`${bridge.bridgeName}.${method}`] = cap.available
      }
    }
    
    return capabilities
  }
  
  /**
   * Generate JavaScript to inject into iframe for capability detection
   * (Used if SDK doesn't support postMessage detection)
   */
  getCapabilitiesScript(): string {
    const availableBridges = this.getAvailableBridges()
    const capabilities = this.getCapabilities()
    
    return `
      window.QuappEnvironment = 'web-frame';
      window.QuappAvailableBridges = ${JSON.stringify(availableBridges)};
      window.QuappDeviceCapabilities = ${JSON.stringify(capabilities)};
      window.QuappWebFrameReady = true;
    `
  }
  
  /**
   * Clean up all resources
   */
  cleanup(): void {
    // Remove message listener
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
    }
    
    // Clean up all bridges
    for (const bridge of this.bridges.values()) {
      bridge.cleanup()
    }
    
    this.bridges.clear()
    this.iframeWindow = null
  }
}

// Singleton instance for easy access
let bridgeManagerInstance: BridgeManager | null = null

export function getBridgeManager(options?: BridgeManagerOptions): BridgeManager {
  if (!bridgeManagerInstance) {
    bridgeManagerInstance = new BridgeManager(options)
  }
  return bridgeManagerInstance
}

export function destroyBridgeManager(): void {
  if (bridgeManagerInstance) {
    bridgeManagerInstance.cleanup()
    bridgeManagerInstance = null
  }
}

